import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/LanguageContext";

// Today's date in Eastern time (box numbers restart every day, Eastern).
export const boxToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
// Default arrival: 7 days after shipping (Eastern calendar day).
export const ETA_DAYS = 7;
export const defaultEta = () => {
  const d = new Date();
  d.setDate(d.getDate() + ETA_DAYS);
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
};

// Make Box dialog: new box (editable number + note) or add the ticked orders
// to an existing open box. Optional tracking # ships the box right away.
export default function MakeBoxDialog({ open, onOpenChange, count, openBoxes, onConfirm, busy }) {
  const { t } = useT();
  const today = boxToday();
  const nextNumber = Math.max(0, ...openBoxes.filter((b) => b.date === today).map((b) => Number(b.number) || 0)) + 1;
  const [mode, setMode] = useState("new");
  const [number, setNumber] = useState(String(nextNumber));
  const [note, setNote] = useState("");
  const [boxId, setBoxId] = useState(openBoxes[0]?.id || "");
  const [tracking, setTracking] = useState("");
  const [eta, setEta] = useState(defaultEta());

  useEffect(() => {
    if (open) { setMode("new"); setNumber(String(nextNumber)); setNote(""); setBoxId(openBoxes[0]?.id || ""); setTracking(""); setEta(defaultEta()); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sameNumberToday = openBoxes.find((b) => b.date === today && String(b.number) === number.trim());
  const shipNow = tracking.trim().length > 0;
  const confirm = () => {
    const ship = shipNow ? { tracking: tracking.trim(), eta: eta || undefined } : {};
    if (mode === "existing") {
      if (!boxId) return;
      onConfirm({ box_id: boxId, note: note.trim() || undefined, ...ship });
    } else {
      const n = parseInt(number, 10);
      if (!n || n < 1) return;
      onConfirm({ box_number: n, note: note.trim() || undefined, ...ship });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="make-box-dialog">
        <DialogHeader>
          <DialogTitle>📦 {t("Make Box")} · {count} {t("order(s)")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer ${mode === "new" ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
            <input type="radio" name="box-mode" checked={mode === "new"} onChange={() => setMode("new")} data-testid="make-box-mode-new" className="mt-1" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">{t("New box")}</div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs font-mono uppercase text-slate-500">{t("Box #")}</span>
                <Input
                  type="number"
                  min={1}
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onFocus={() => setMode("new")}
                  className="w-24 font-mono h-9"
                  data-testid="make-box-number"
                />
                <span className="text-[11px] text-slate-500 font-mono">{today}</span>
              </div>
              {sameNumberToday && (
                <div className="mt-1.5 text-[11px] text-amber-800" data-testid="make-box-same-number-hint">
                  {t("BOX")} {number} {t("already exists today — the orders will be added to it.")}
                </div>
              )}
            </div>
          </label>

          {openBoxes.length > 0 && (
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer ${mode === "existing" ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
              <input type="radio" name="box-mode" checked={mode === "existing"} onChange={() => setMode("existing")} data-testid="make-box-mode-existing" className="mt-1" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">{t("Add to an existing box")}</div>
                <select
                  value={boxId}
                  onChange={(e) => { setBoxId(e.target.value); setMode("existing"); }}
                  onFocus={() => setMode("existing")}
                  className="mt-2 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm font-mono"
                  data-testid="make-box-existing-select"
                >
                  {openBoxes.map((b) => (
                    <option key={b.id} value={b.id}>{`BOX ${b.number} · ${b.date} · ${b.count} ${t("order(s)")}${b.note ? ` · ${b.note}` : ""}`}</option>
                  ))}
                </select>
              </div>
            </label>
          )}

          <div>
            <div className="text-xs font-mono uppercase text-slate-500 mb-1">{t("Box note")} <span className="normal-case text-slate-400">({t("visible in Orders Received")})</span></div>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={t("e.g. fragile · 2 spacers packs inside · ship with BOX 3")} data-testid="make-box-note" />
          </div>

          <div className={`border rounded-md p-3 ${shipNow ? "border-emerald-400 bg-emerald-50/60" : "border-dashed border-slate-300"}`}>
            <div className="text-xs font-mono uppercase text-slate-500 mb-1">🚚 {t("Tracking #")} <span className="normal-case text-slate-400">({t("optional — ships the box right away")})</span></div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="1Z…"
                className="flex-1 min-w-[200px] font-mono"
                data-testid="make-box-tracking"
              />
              <span className="text-[10px] font-mono uppercase text-slate-500">{t("ETA")}</span>
              <Input
                type="date"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                className="w-[160px] font-mono"
                disabled={!shipNow}
                data-testid="make-box-eta"
              />
            </div>
            <div className="mt-1 text-[11px] text-slate-500">{t("ETA is set automatically to")} {ETA_DAYS} {t("days after shipping — change it if needed.")}</div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="make-box-cancel">{t("Cancel")}</Button>
            <Button onClick={confirm} disabled={busy || (mode === "new" ? !(parseInt(number, 10) >= 1) : !boxId)} className={`${shipNow ? "bg-emerald-600 hover:bg-emerald-700" : "bg-sky-700 hover:bg-sky-800"} text-white`} data-testid="make-box-confirm">
              {mode === "existing" ? `➕ ${t("Add to box")}` : `📦 ${t("Make Box")}`}{shipNow ? ` & 🚚 ${t("Ship")}` : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
